import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useBeforeUnload } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ATID } from "@/lib/adminTestIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MediaPicker from "@/components/admin/MediaPicker";
import FocalPointPicker from "@/components/admin/FocalPointPicker";
import SocialPreviewCard from "@/components/admin/SocialPreviewCard";
import AIBodyAssistant from "@/components/admin/AIBodyAssistant";
import SEOSection from "@/components/admin/SEOSection";
import AiAssistButton from "@/components/admin/AiAssistButton";
import { Save, Send, Eye, ArrowLeft, Loader2, Image as ImageIcon, X, Sparkles } from "lucide-react";

const STATUSES = [
  { value: "draft", label: "Borrador" },
  { value: "pending", label: "Pendiente de revisión" },
  { value: "scheduled", label: "Programada" },
  { value: "published", label: "Publicada" },
  { value: "archived", label: "Archivada" },
];

const CONTENT_TYPES = [
  { value: "article", label: "Noticia" },
  { value: "breaking", label: "Último momento" },
  { value: "live", label: "En vivo" },
  { value: "opinion", label: "Opinión" },
  { value: "interview", label: "Entrevista" },
  { value: "video", label: "Video" },
  { value: "gallery", label: "Galería" },
  { value: "press_release", label: "Comunicado" },
  { value: "sponsored", label: "Contenido patrocinado" },
];

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 120);
}

// Convert ISO UTC string to local "YYYY-MM-DDTHH:mm" (for datetime-local input, in Argentina TZ)
function toLocalInput(isoString) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    // Convert to Ushuaia (UTC-3). Since browser could be any TZ, we build parts using Intl.
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "America/Argentina/Ushuaia",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const parts = fmt.formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch { return ""; }
}

// Convert "YYYY-MM-DDTHH:mm" (assumed Argentina/Ushuaia local time) to UTC ISO string
function fromLocalInput(v) {
  if (!v) return null;
  // Argentina is UTC-3, no DST — so local time + 03:00 = UTC
  return new Date(`${v}:00-03:00`).toISOString();
}

export default function AdminArticleEditorPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { hasPerm, isAdmin } = useAuth();

  const canPublish = isAdmin || hasPerm("articles.publish");
  const canSubmit = isAdmin || hasPerm("articles.submit_for_review");
  const canDelete = isAdmin || hasPerm("articles.delete");
  const draftOnly = !isAdmin && hasPerm("articles.draft_only");

  const availableStatuses = useMemo(() => {
    if (draftOnly) return STATUSES.filter((s) => s.value === "draft");
    return STATUSES.filter((s) => {
      if (s.value === "published" || s.value === "scheduled") return canPublish;
      if (s.value === "pending") return canSubmit;
      if (s.value === "archived") return canDelete;
      return true;
    });
  }, [draftOnly, canPublish, canSubmit, canDelete]);

  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [contentHistory, setContentHistory] = useState([]);
  const [suggestedTitles, setSuggestedTitles] = useState([]);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    summary: "",
    content: "",
    main_image: "",
    image_caption: "",
    image_credit: "",
    image_focal_point: "",
    author_id: "",
    main_category_id: "",
    secondary_category_ids: [],
    tags: "",
    status: "draft",
    published_at: "",
    scheduled_at: "",
    is_featured: false,
    is_breaking: false,
    breaking_duration_hours: 24,
    breaking_started_at: null,
    breaking_expires_at: null,
    content_type: "article",
    source_name: "",
    source_url: "",
    // SEO
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    og_image: "",
    canonical_url: "",
    // Etapa 5 — distribución multicanal
    publish_to_facebook: "auto",
    publish_to_instagram: "auto",
  });

  const originalRef = useRef(form);

  const aiPayload = () => ({
    title: form.title,
    summary: form.summary,
    content: form.content,
    tags: form.tags,
    category: form.main_category_id,
  });

  const setField = (patch) => {
    setForm((f) => {
      const next = { ...f, ...patch };
      // Auto-slug while creating and untouched
      if (!slugManuallyEdited && !isEdit && "title" in patch) {
        next.slug = slugify(patch.title);
      }
      return next;
    });
    setDirty(true);
  };

  useEffect(() => {
    Promise.all([api.adminListCategories(), api.adminListAuthors()])
      .then(([cats, auths]) => {
        setCategories(cats);
        setAuthors(auths);
      })
      .catch(() => toast.error("No se pudieron cargar categorías/autores."));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.adminGetArticle(id)
      .then((doc) => {
        const populated = {
          title: doc.title || "",
          slug: doc.slug || "",
          summary: doc.summary || "",
          content: doc.content || "",
          main_image: doc.main_image || "",
          image_caption: doc.image_caption || "",
          image_credit: doc.image_credit || "",
          image_focal_point: doc.image_focal_point || "",
          author_id: doc.author_id || "",
          main_category_id: doc.main_category_id || "",
          secondary_category_ids: doc.secondary_category_ids || [],
          tags: (doc.tags || []).join(", "),
          status: doc.status || "draft",
          published_at: toLocalInput(doc.published_at),
          scheduled_at: toLocalInput(doc.scheduled_at),
          is_featured: !!doc.is_featured,
          is_breaking: !!doc.is_breaking,
          breaking_duration_hours: 24,
          breaking_started_at: doc.breaking_started_at || null,
          breaking_expires_at: doc.breaking_expires_at || null,
          content_type: doc.content_type || "article",
          source_name: doc.source_name || "",
          source_url: doc.source_url || "",
          // SEO
          seo_title: doc.seo_title || "",
          seo_description: doc.seo_description || "",
          seo_keywords: (doc.seo_keywords || []).join(", "),
          og_image: doc.og_image || "",
          canonical_url: doc.canonical_url || "",
          publish_to_facebook: doc.publish_to_facebook || "auto",
          publish_to_instagram: doc.publish_to_instagram || "auto",
        };
        setForm(populated);
        originalRef.current = populated;
        setSlugManuallyEdited(true); // don't auto-regenerate in edit
      })
      .catch(() => toast.error("No se pudo cargar la noticia."))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // Warn about unsaved changes
  useBeforeUnload(
    React.useCallback((event) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    }, [dirty])
  );

  // Autosave draft (local only) every 30s while dirty on create
  const autoSaveKey = useMemo(() => (isEdit ? `sp_autosave_edit_${id}` : "sp_autosave_new"), [isEdit, id]);
  useEffect(() => {
    if (!dirty) return;
    const t = setInterval(() => {
      try { localStorage.setItem(autoSaveKey, JSON.stringify(form)); } catch { /* ignore */ }
    }, 30_000);
    return () => clearInterval(t);
  }, [dirty, form, autoSaveKey]);

  const validate = (targetStatus) => {
    if (!form.title.trim()) return "Falta el título.";
    if (!form.main_category_id) return "Elegí una categoría principal.";
    if (!form.author_id) return "Elegí un autor.";
    if (targetStatus === "scheduled" && !form.scheduled_at) return "Indicá fecha y hora de programación.";
    return null;
  };

  const buildPayload = (overrideStatus) => {
    const finalStatus = overrideStatus || form.status;
    return {
      title: form.title.trim(),
      slug: form.slug ? slugify(form.slug) : "",
      summary: form.summary,
      content: form.content,
      main_image: form.main_image,
      image_caption: form.image_caption || null,
      image_credit: form.image_credit || null,
      image_focal_point: form.image_focal_point || null,
      author_id: form.author_id,
      main_category_id: form.main_category_id,
      secondary_category_ids: form.secondary_category_ids,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: finalStatus,
      published_at: fromLocalInput(form.published_at),
      scheduled_at: finalStatus === "scheduled" ? fromLocalInput(form.scheduled_at) : (form.scheduled_at ? fromLocalInput(form.scheduled_at) : null),
      is_featured: form.is_featured,
      is_breaking: form.is_breaking,
      // If the flag is being (re)activated, tell the backend for how long.
      breaking_duration_hours: form.is_breaking ? Number(form.breaking_duration_hours || 24) : null,
      content_type: form.content_type,
      origin_type: "manual",
      source_name: form.source_name || null,
      source_url: form.source_url || null,
      // SEO
      seo_title: form.seo_title || null,
      seo_description: form.seo_description || null,
      seo_keywords: form.seo_keywords ? form.seo_keywords.split(",").map((t) => t.trim()).filter(Boolean) : [],
      og_image: form.og_image || null,
      canonical_url: form.canonical_url || null,
      publish_to_facebook: form.publish_to_facebook || "auto",
      publish_to_instagram: form.publish_to_instagram || "auto",
    };
  };

  const save = async (overrideStatus) => {
    const err = validate(overrideStatus || form.status);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = buildPayload(overrideStatus);
      const saved = isEdit
        ? await api.adminUpdateArticle(id, payload)
        : await api.adminCreateArticle(payload);
      setDirty(false);
      try { localStorage.removeItem(autoSaveKey); } catch { /* ignore */ }
      toast.success(overrideStatus === "published" ? "Noticia publicada." : "Cambios guardados.");
      if (!isEdit) {
        navigate(`/admin/noticias/${saved.id}`, { replace: true });
      } else {
        // update local status/slug
        setField({ status: saved.status, slug: saved.slug, published_at: toLocalInput(saved.published_at) });
        setDirty(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-textgray">Cargando editor…</div>;
  }

  return (
    <div data-testid={ATID.editor}>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div className="min-w-0">
          <Link to="/admin/noticias" className="text-xs uppercase tracking-widest text-turquoise font-semibold inline-flex items-center gap-1 hover:text-petroleum">
            <ArrowLeft className="w-3.5 h-3.5" /> Volver
          </Link>
          <h1 className="font-serif font-black text-3xl sm:text-4xl text-blueblack line-clamp-2 mt-1">
            {isEdit ? "Editar noticia" : "Nueva noticia"}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isEdit && form.status === "published" && form.slug && (
            <a
              href={`/noticia/${form.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-softblue text-nightblue hover:bg-lightgray h-10 px-4 text-xs font-semibold uppercase tracking-widest transition-colors"
            >
              <Eye className="w-4 h-4" /> Previsualizar
            </a>
          )}
          <Button
            type="button"
            data-testid={ATID.editorSaveDraft}
            disabled={saving}
            onClick={() => save("draft")}
            variant="outline"
            className="rounded-none h-10 border-softblue text-nightblue hover:bg-lightgray uppercase text-xs tracking-widest font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar borrador
          </Button>
          <Button
            type="button"
            data-testid={ATID.editorSubmit}
            disabled={saving}
            onClick={() => save()}
            className="bg-nightblue hover:bg-blueblack text-white rounded-none h-10 px-5 uppercase text-xs tracking-widest font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Guardar cambios
          </Button>
        </div>
      </div>

      {dirty && (
        <div className="mb-4 bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-2 text-sm">
          Hay cambios sin guardar.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Título *</Label>
              <AiAssistButton
                task="titles"
                label="Sugerir titulares"
                testId={ATID.aiTitles}
                getPayload={aiPayload}
                onResult={(r) => setSuggestedTitles(r?.titles || [])}
              />
            </div>
            <Input
              data-testid={ATID.editorTitle}
              value={form.title}
              onChange={(e) => setField({ title: e.target.value })}
              placeholder="Título de la noticia"
              className="mt-2 h-12 text-lg bg-white border-softblue"
            />
            {suggestedTitles.length > 0 && (
              <div className="mt-2 border border-turquoise/40 bg-turquoise/5 p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-textgray font-semibold flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Titulares sugeridos
                  </span>
                  <button type="button" className="text-xs text-textgray hover:text-nightblue" onClick={() => setSuggestedTitles([])}>Cerrar</button>
                </div>
                {suggestedTitles.map((t, i) => (
                  <button
                    key={`title-${i}-${String(t).slice(0, 20)}`}
                    type="button"
                    data-testid={ATID.aiTitlesPick(i)}
                    onClick={() => { setField({ title: t }); setSuggestedTitles([]); }}
                    className="block w-full text-left text-sm text-nightblue hover:bg-white/70 px-2 py-1 rounded"
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Slug (URL)</Label>
            <Input
              data-testid={ATID.editorSlug}
              value={form.slug}
              onChange={(e) => { setField({ slug: e.target.value }); setSlugManuallyEdited(true); }}
              placeholder="slug-de-la-noticia"
              className="mt-2 h-10 bg-white border-softblue font-mono text-sm"
            />
            <p className="text-xs text-textgray mt-1">Se genera automáticamente desde el título. También podés editarlo.</p>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Bajada</Label>
              <AiAssistButton
                task="summary"
                label="Generar bajada"
                testId={ATID.aiSummary}
                getPayload={aiPayload}
                onResult={(r) => r?.summary && setField({ summary: r.summary })}
              />
            </div>
            <Textarea
              data-testid={ATID.editorSummary}
              value={form.summary}
              onChange={(e) => setField({ summary: e.target.value })}
              rows={3}
              placeholder="Resumen breve que acompaña el título."
              className="mt-2 bg-white border-softblue"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Cuerpo *</Label>
            <div className="mt-2 space-y-2">
              <AIBodyAssistant
                getPayload={() => ({
                  title: form.title,
                  summary: form.summary,
                  content: form.content,
                  source_url: form.source_url,
                  source_name: form.source_name,
                  editor_notes: form.editor_notes,
                  category: (categories.find((c) => c.id === form.main_category_id) || {}).slug,
                })}
                onApply={(html) => setField({ content: html })}
                onPushVersion={(prev, next, action) => {
                  setContentHistory((h) => [
                    { id: Date.now(), action, at: new Date().toISOString(), prev, next },
                    ...h,
                  ].slice(0, 8));
                }}
              />
              <RichTextEditor
                value={form.content}
                onChange={(html) => setField({ content: html })}
                placeholder="Escribí el cuerpo de la noticia…"
              />
              {contentHistory.length > 0 && (
                <div className="border border-softblue p-2 bg-white text-[11px] space-y-1">
                  <div className="uppercase tracking-widest font-bold text-nightblue text-[10px]">Historial de cambios IA</div>
                  {contentHistory.map((v) => (
                    <div key={v.id} className="flex items-center justify-between py-1 border-t border-softblue first:border-t-0">
                      <span className="text-textgray">
                        <b>{v.action}</b> · {new Date(v.at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          if (!confirm("¿Restaurar la versión anterior del cuerpo?")) return;
                          setField({ content: v.prev });
                          setContentHistory((h) => h.filter((x) => x.id !== v.id));
                          toast.success("Versión anterior restaurada");
                        }}
                        className="text-turquoise hover:text-nightblue uppercase tracking-widest font-semibold text-[10px]"
                      >
                        Restaurar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Etiquetas</Label>
                <AiAssistButton
                  task="tags"
                  label="Sugerir etiquetas"
                  testId={ATID.aiTags}
                  getPayload={aiPayload}
                  onResult={(r) => Array.isArray(r?.tags) && setField({ tags: r.tags.join(", ") })}
                />
              </div>
              <Input
                data-testid={ATID.editorTags}
                value={form.tags}
                onChange={(e) => setField({ tags: e.target.value })}
                placeholder="separadas, por, coma"
                className="mt-2 h-10 bg-white border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Tipo de contenido</Label>
              <select
                data-testid={ATID.editorContentType}
                value={form.content_type}
                onChange={(e) => setField({ content_type: e.target.value })}
                className="mt-2 h-10 w-full bg-white border border-softblue px-3 text-sm text-nightblue"
              >
                {CONTENT_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Fuente</Label>
              <Input
                data-testid={ATID.editorSourceName}
                value={form.source_name}
                onChange={(e) => setField({ source_name: e.target.value })}
                placeholder="Nombre de la fuente"
                className="mt-2 h-10 bg-white border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">URL de la fuente</Label>
              <Input
                data-testid={ATID.editorSourceUrl}
                value={form.source_url}
                onChange={(e) => setField({ source_url: e.target.value })}
                placeholder="https://…"
                className="mt-2 h-10 bg-white border-softblue"
              />
            </div>
          </div>

          {/* SEO section */}
          <SEOSection
            form={form}
            setField={setField}
            currentTitle={form.title}
            currentSummary={form.summary}
            currentMainImage={form.main_image}
            aiPayload={aiPayload}
          />
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="bg-white border border-softblue p-5">
            <h3 className="font-serif text-lg font-bold text-blueblack mb-3">Estado</h3>
            <select
              data-testid={ATID.editorStatus}
              value={form.status}
              onChange={(e) => setField({ status: e.target.value })}
              className="h-10 w-full bg-lightgray border border-softblue px-3 text-sm text-nightblue"
            >
              {availableStatuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <div className="mt-4 space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Fecha de publicación</Label>
                <Input
                  data-testid={ATID.editorPublishedAt}
                  type="datetime-local"
                  value={form.published_at}
                  onChange={(e) => setField({ published_at: e.target.value })}
                  className="mt-2 h-10 bg-lightgray border-softblue"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Programada para</Label>
                <Input
                  data-testid={ATID.editorScheduledAt}
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setField({ scheduled_at: e.target.value })}
                  className="mt-2 h-10 bg-lightgray border-softblue"
                />
                <p className="text-[11px] text-textgray mt-1">Requerida si el estado es "Programada".</p>
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-softblue space-y-3">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span className="text-nightblue font-semibold">Noticia destacada</span>
                <Switch
                  data-testid={ATID.editorFeatured}
                  checked={form.is_featured}
                  onCheckedChange={(v) => setField({ is_featured: !!v })}
                />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span className="text-nightblue font-semibold">Último momento</span>
                <Switch
                  data-testid={ATID.editorBreaking}
                  checked={form.is_breaking}
                  onCheckedChange={(v) => setField({ is_breaking: !!v })}
                />
              </label>
              {form.is_breaking && (
                <div className="mt-2 space-y-2 border-t border-softblue pt-3">
                  <Label className="text-[10px] uppercase tracking-widest text-textgray font-semibold">Duración</Label>
                  <select
                    data-testid="editor-breaking-duration"
                    value={String(form.breaking_duration_hours || 24)}
                    onChange={(e) => setField({ breaking_duration_hours: parseFloat(e.target.value) })}
                    className="h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue"
                  >
                    <option value="1">1 hora</option>
                    <option value="3">3 horas</option>
                    <option value="6">6 horas</option>
                    <option value="12">12 horas</option>
                    <option value="24">24 horas (default)</option>
                    <option value="48">48 horas</option>
                    <option value="0.0333">2 minutos (prueba)</option>
                  </select>
                  {form.breaking_expires_at && (() => {
                    const exp = new Date(form.breaking_expires_at);
                    const now = new Date();
                    const active = exp > now;
                    const label = exp.toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
                    return (
                      <div className={`text-[11px] p-2 border ${active ? "border-turquoise/40 bg-turquoise/10 text-nightblue" : "border-red-300 bg-red-50 text-red-700"}`}>
                        {active ? "Último momento hasta:" : "Ya expiró:"} <b>{label}</b>
                        <div className="text-[10px] text-textgray mt-0.5">Al guardar con la duración de arriba se reinicia el contador.</div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-softblue space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold">Distribución en redes</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="space-y-1">
                  <span className="text-nightblue font-semibold">Facebook</span>
                  <select
                    data-testid="editor-publish-to-facebook"
                    value={form.publish_to_facebook || "auto"}
                    onChange={(e) => setField({ publish_to_facebook: e.target.value })}
                    className="h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue"
                  >
                    <option value="auto">Auto (motor decide)</option>
                    <option value="true">Sí (forzar)</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-nightblue font-semibold">Instagram</span>
                  <select
                    data-testid="editor-publish-to-instagram"
                    value={form.publish_to_instagram || "auto"}
                    onChange={(e) => setField({ publish_to_instagram: e.target.value })}
                    className="h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue"
                  >
                    <option value="auto">Auto (motor decide)</option>
                    <option value="true">Sí (forzar)</option>
                    <option value="false">No</option>
                  </select>
                </label>
              </div>
              <div className="text-[10px] text-textgray">Auto: motor decide según score, cupo y contenido visual. Sí: fuerza elegibilidad. No: bloquea canal.</div>
            </div>
            <div className="mt-5 pt-4 border-t border-softblue">
              <Button
                type="button"
                onClick={() => save("published")}
                disabled={saving}
                className="w-full bg-watergreen hover:bg-turquoise text-nightblue rounded-none h-10 uppercase text-xs tracking-widest font-bold"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Publicar ahora
              </Button>
            </div>
          </div>

          <div className="bg-white border border-softblue p-5">
            <h3 className="font-serif text-lg font-bold text-blueblack mb-3">Categoría y autor</h3>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Categoría principal *</Label>
            <select
              data-testid={ATID.editorCategory}
              value={form.main_category_id}
              onChange={(e) => setField({ main_category_id: e.target.value })}
              className="mt-2 h-10 w-full bg-lightgray border border-softblue px-3 text-sm text-nightblue"
            >
              <option value="">Seleccionar…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <Label className="mt-4 block text-xs uppercase tracking-widest text-textgray font-semibold">Ubicación</Label>
            <select
              data-testid="editor-location"
              value={(() => {
                const loc = categories.find(
                  (c) => c.is_location && form.secondary_category_ids?.includes(c.id)
                );
                return loc ? loc.id : "";
              })()}
              onChange={(e) => {
                const val = e.target.value;
                const locIds = categories.filter((c) => c.is_location).map((c) => c.id);
                const withoutLoc = (form.secondary_category_ids || []).filter((id) => !locIds.includes(id));
                setField({ secondary_category_ids: val ? [val, ...withoutLoc] : withoutLoc });
              }}
              className="mt-2 h-10 w-full bg-lightgray border border-softblue px-3 text-sm text-nightblue"
            >
              <option value="">Sin ubicación definida</option>
              {categories.filter((c) => c.is_location).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="flex items-center justify-between mt-1">
              <p className="text-[11px] text-textgray">Dónde ocurre. Aparecerá en /categoria/{"{ciudad}"}.</p>
              <button
                type="button"
                data-testid="editor-location-detect"
                onClick={async () => {
                  try {
                    const stripHtml = (h) => (h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
                    const res = await api.adminDetectLocation({
                      title: form.title || "",
                      summary: form.summary || "",
                      body: stripHtml(form.content).slice(0, 5000),
                    });
                    const slugs = res?.suggested_locations || [];
                    if (!slugs.length) { toast.info("No se detectó ubicación clara en el texto"); return; }
                    const locCats = categories.filter((c) => c.is_location && slugs.includes(c.slug));
                    if (!locCats.length) return;
                    const first = locCats[0];
                    const locIds = categories.filter((c) => c.is_location).map((c) => c.id);
                    const withoutLoc = (form.secondary_category_ids || []).filter((id) => !locIds.includes(id));
                    setField({ secondary_category_ids: [first.id, ...withoutLoc] });
                    toast.success(`Ubicación detectada: ${first.name}`);
                  } catch {
                    toast.error("Error al detectar ubicación");
                  }
                }}
                className="text-[11px] uppercase tracking-widest font-bold text-turquoise hover:text-nightblue"
              >
                Detectar del texto
              </button>
            </div>

            <Label className="mt-4 block text-xs uppercase tracking-widest text-textgray font-semibold">Categorías secundarias</Label>
            <select
              multiple
              data-testid="editor-secondary"
              value={form.secondary_category_ids}
              onChange={(e) => {
                const picked = Array.from(e.target.selectedOptions).map((o) => o.value);
                // Preserve any active location selection (managed separately above)
                const locIds = categories.filter((c) => c.is_location).map((c) => c.id);
                const activeLoc = (form.secondary_category_ids || []).find((id) => locIds.includes(id));
                const withoutLoc = picked.filter((id) => !locIds.includes(id));
                setField({ secondary_category_ids: activeLoc ? [activeLoc, ...withoutLoc] : withoutLoc });
              }}
              className="mt-2 h-24 w-full bg-lightgray border border-softblue px-2 py-1 text-sm text-nightblue"
            >
              {categories
                .filter((c) => !c.is_location && c.id !== form.main_category_id)
                .map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
            </select>
            <p className="text-[11px] text-textgray mt-1">Temáticas adicionales. Ctrl/Cmd para varias.</p>

            <Label className="mt-4 block text-xs uppercase tracking-widest text-textgray font-semibold">Autor *</Label>
            <select
              data-testid={ATID.editorAuthor}
              value={form.author_id}
              onChange={(e) => setField({ author_id: e.target.value })}
              className="mt-2 h-10 w-full bg-lightgray border border-softblue px-3 text-sm text-nightblue"
            >
              <option value="">Seleccionar…</option>
              {authors.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          <div className="bg-white border border-softblue p-5">
            <h3 className="font-serif text-lg font-bold text-blueblack mb-3">Imagen principal</h3>
            <MediaMainImagePicker
              url={form.main_image}
              caption={form.image_caption}
              credit={form.image_credit}
              onSelect={(m) => setField({
                main_image: m.url,
                image_caption: form.image_caption || m.caption || "",
                image_credit: form.image_credit || m.credit || "",
              })}
              onRemove={() => setField({ main_image: "" })}
              onCaptionChange={(v) => setField({ image_caption: v })}
              onCreditChange={(v) => setField({ image_credit: v })}
              onUrlChange={(v) => setField({ main_image: v })}
            />
            {form.main_image && (
              <FocalPointPicker
                url={form.main_image}
                value={form.image_focal_point || "50% 50%"}
                onChange={(v) => setField({ image_focal_point: v })}
              />
            )}
          </div>

          <SocialPreviewCard
            title={form.seo_title || form.title}
            description={form.seo_description || form.summary}
            image={form.main_image}
            url={form.slug ? `https://surprimicia.com.ar/noticia/${form.slug}` : null}
            siteHost="surprimicia.com.ar"
          />
        </aside>
      </div>
    </div>
  );
}

function MediaMainImagePicker({ url, caption, credit, onSelect, onRemove, onCaptionChange, onCreditChange, onUrlChange }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex flex-col gap-2">
        {url ? (
          <div className="relative border border-softblue overflow-hidden bg-lightgray aspect-[16/9]">
            <img src={url} alt="Imagen principal" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={onRemove}
              data-testid={ATID.editorMainImageRemove}
              className="absolute top-2 right-2 h-8 w-8 bg-white/95 border border-softblue text-nightblue hover:bg-red-50 hover:text-red-600 inline-flex items-center justify-center"
              title="Quitar imagen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="border border-dashed border-softblue bg-lightgray p-6 text-center text-sm text-textgray">
            <ImageIcon className="w-8 h-8 mx-auto text-textgray" />
            <p className="mt-2">Sin imagen principal seleccionada.</p>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            data-testid={ATID.mediaPickerOpen}
            onClick={() => setOpen(true)}
            className="flex-1 bg-nightblue hover:bg-blueblack text-white rounded-none h-10 uppercase text-xs tracking-widest font-semibold"
          >
            <ImageIcon className="w-4 h-4 mr-2" /> {url ? "Reemplazar" : "Elegir de biblioteca"}
          </Button>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">URL de la imagen</Label>
          <Input
            data-testid={ATID.editorMainImage}
            value={url || ""}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="https://…"
            className="mt-1 h-9 bg-lightgray border-softblue font-mono text-xs"
          />
        </div>
        <div className="grid grid-cols-1 gap-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Pie de imagen</Label>
            <Input
              data-testid={ATID.editorImageCaption}
              value={caption || ""}
              onChange={(e) => onCaptionChange(e.target.value)}
              className="mt-1 h-9 bg-lightgray border-softblue text-sm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Crédito</Label>
            <Input
              data-testid={ATID.editorImageCredit}
              value={credit || ""}
              onChange={(e) => onCreditChange(e.target.value)}
              className="mt-1 h-9 bg-lightgray border-softblue text-sm"
            />
          </div>
        </div>
      </div>
      <MediaPicker
        open={open}
        onOpenChange={setOpen}
        onSelect={onSelect}
        title="Elegir imagen principal"
        accept="image"
      />
    </>
  );
}
